import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import QuickAddMenu from '../../components/ui/QuickAddMenu';
import Button from '../../components/ui/Button';
import CustomerFilters from './components/CustomerFilters';
import CustomerTable from './components/CustomerTable';
import CustomerMobileCard from './components/CustomerMobileCard';
import CustomerProfile from './components/CustomerProfile';
import AddCustomerModal from './components/AddCustomerModal';
import Icon from '../../components/AppIcon';
import PermissionDenied from '../../components/ui/PermissionDenied';
import { usePermissions } from '../../hooks/usePermissions';
import { customerService } from '../../services/customerService';
import { exportToCSV, CUSTOMER_CSV_COLUMNS } from '../../utils/exportToCSV';
import SEO from '../../components/SEO';


const Customers = () => {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [aduTypeFilter, setAduTypeFilter] = useState('all');
  const [leadSourceFilter, setLeadSourceFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permDeniedMsg, setPermDeniedMsg] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const data = await customerService?.getAll();
      setCustomers(data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedCustomers = useMemo(() => {
    let filtered = customers?.filter(customer => {
      const matchesSearch = !searchQuery || 
        customer?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        customer?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        customer?.phone?.includes(searchQuery);
      
      const matchesStatus = statusFilter === 'all' || customer?.projectStatus === statusFilter;
      const matchesAduType = aduTypeFilter === 'all' || customer?.aduType === aduTypeFilter;
      const matchesLeadSource = leadSourceFilter === 'all' || customer?.leadSource === leadSourceFilter;
      
      return matchesSearch && matchesStatus && matchesAduType && matchesLeadSource;
    });

    filtered?.sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];
      
      if (sortField === 'name') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [customers, searchQuery, statusFilter, aduTypeFilter, leadSourceFilter, sortField, sortDirection]);

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleCloseProfile = () => {
    setSelectedCustomer(null);
  };

  const handleCustomerUpdate = () => {
    loadCustomers();
    setSelectedCustomer(null);
  };

  const showPermDenied = (msg) => {
    setPermDeniedMsg(msg);
    setTimeout(() => setPermDeniedMsg(''), 4000);
  };

  const handleAddCustomer = () => {
    if (!canCreate) {
      showPermDenied('You have viewer access only. Contact an admin to add customers.');
      return;
    }
    setShowAddModal(true);
  };

  const handleAddSuccess = () => {
    loadCustomers();
    setShowAddModal(false);
  };

  const handleExportCSV = () => {
    exportToCSV(
      filteredAndSortedCustomers,
      CUSTOMER_CSV_COLUMNS,
      `customers_export_${new Date()?.toISOString()?.slice(0, 10)}`
    );
  };

  return (
    <>
      <SEO
        title="Customer Management — ADU Client CRM"
        description="Manage all your ADU construction customers in one place. Track leads, ADU types, project status, and client communications with ADU Flow's customer CRM."
        canonical="/customers"
        keywords="ADU customers, client management, CRM, construction clients, customer tracking"
        noIndex={true}
      />
      <Header />
      <QuickAddMenu />
      <div className="main-content">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">Customers</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">
                Manage your ADU construction clients and track project relationships
              </p>
            </div>
            <div className="flex items-center gap-3">
              {canCreate && (
                <Button
                  variant="default"
                  size="default"
                  iconName="UserPlus"
                  iconPosition="left"
                  onClick={handleAddCustomer}>
                  Add Customer
                </Button>
              )}
              <Button
                variant="outline"
                size="default"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportCSV}
                disabled={!filteredAndSortedCustomers?.length}>
                Export CSV
              </Button>
            </div>
          </div>

          {permDeniedMsg && (
            <PermissionDenied message={permDeniedMsg} className="mb-4" />
          )}

          {/* Filters */}
          <CustomerFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            aduTypeFilter={aduTypeFilter}
            onAduTypeChange={setAduTypeFilter}
            leadSourceFilter={leadSourceFilter}
            onLeadSourceChange={setLeadSourceFilter}
            resultCount={filteredAndSortedCustomers?.length} />
          

          {/* Loading State */}
          {loading ? (
            <div className="bg-card rounded-xl p-12 text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading customers...</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block">
                <CustomerTable
                  customers={filteredAndSortedCustomers}
                  onCustomerClick={handleCustomerClick}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  canEdit={canEdit}
                  canDelete={canDelete} />
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {filteredAndSortedCustomers?.length === 0 ?
                <div className="bg-card rounded-xl p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                      <Icon name="Users" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">No customers found</h3>
                    <p className="text-sm text-muted-foreground">Try adjusting your filters or add a new customer to get started.</p>
                  </div> :

                filteredAndSortedCustomers?.map((customer) =>
                <CustomerMobileCard
                  key={customer?.id}
                  customer={customer}
                  onClick={() => handleCustomerClick(customer)} />
                )
                }
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add Customer Modal */}
      <AddCustomerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />

      {/* Customer Profile Modal */}
      {selectedCustomer &&
      <CustomerProfile
        customer={selectedCustomer}
        onClose={handleCloseProfile}
        onUpdate={handleCustomerUpdate}
        canEdit={canEdit}
        canDelete={canDelete} />
      }
    </>
  );

};

export default Customers;